import * as React from 'react'
import {Box, TextField} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE} from '../../../../../styles/foundation/colors'
import {round2} from '../../weightMath'

/**
 * Weight Freeze (v2) — Packing. The editable number cell, ported from the
 * mockup's `PkNum`.
 *
 * This is behaviour, not decoration. The cells it renders are pack dead
 * weights and carton L/B/H, and every one of them cascades into billed
 * chargeable weight for every SKU and combo sharing that packing material.
 * A plain MUI `TextField` is a controlled input with no draft/commit/revert
 * semantics: `onChange` fires per keystroke, so typing "1" on the way to
 * "12.5" would push a dead weight of 1 kg through the model and back out
 * to every dependent figure. So the draft lives here, locally, and
 * `onCommit` fires only when a valid value is actually committed.
 *
 *   - blur commits, Enter commits
 *   - Escape reverts to the last committed value
 *   - blank, non-numeric or below-`min` (default 0) silently reverts
 *   - a commit equal to the value already held fires nothing
 *
 * Committing rounds to 2dp via the module's own `round2`, which is the
 * precision `chargeableKg` works in — it also keeps float noise
 * (0.1 + 0.2) out of the merchant record.
 *
 * The record, not this component, is the source of truth: after a commit
 * the draft re-syncs from the incoming `value` prop, so a parent that
 * rejects or clamps the value snaps the cell back to what was really
 * stored rather than leaving a lie on screen.
 *
 * @param {number}   value    the committed value, owned by the caller
 * @param {number}  [step]    native arrow-key increment (default 0.05)
 * @param {number}  [min]     lowest acceptable value (default 0)
 * @param {string}  [suffix]  unit shown after the field, e.g. 'kg', 'cm'.
 *                           Decorative and `aria-hidden`, so `label` must
 *                           carry the unit itself ('Dead weight in kg').
 * @param {string}   label    accessible name for the input (required)
 * @param {Function} [onCommit] called with the committed number, never a draft
 */

const asText = (v) => (v == null || Number.isNaN(v) ? '' : String(v))

export default function NumberCell({value, step, min, suffix, label, onCommit}) {
  const [draft, setDraft] = React.useState(() => asText(value))
  const [editing, setEditing] = React.useState(false)

  // The last value we know the record holds. Kept in a ref, not state,
  // because Enter commits and then blurs — the blur handler runs
  // synchronously inside the same event, before any re-render, so a
  // `value`-prop comparison there would still be reading the pre-commit
  // prop and would fire `onCommit` a second time for the same edit.
  const committed = React.useRef(value)

  React.useEffect(() => {
    committed.current = value
    if (!editing) setDraft(asText(value))
  }, [value, editing])

  const revert = () => {
    setEditing(false)
    setDraft(asText(committed.current))
  }

  const commit = () => {
    const raw = draft.trim()
    const next = Number(raw)
    const floor = min == null ? 0 : min
    // Blank, half-typed ("1.2.3" reads back as '' from a number input),
    // or below the floor: revert without telling the caller anything.
    if (raw === '' || !Number.isFinite(next) || next < floor) {
      revert()
      return
    }
    const rounded = round2(next)
    if (rounded === committed.current) {
      revert()
      return
    }
    committed.current = rounded
    setEditing(false)
    setDraft(asText(rounded))
    if (onCommit) onCommit(rounded)
  }

  const handleKeyDown = (event) => {
    // Never let a keystroke reach the row. Table rows in this module are
    // keyboard-activatable (Enter/Space expand or open a drawer), and
    // Escape belongs to whatever drawer may be hosting the table — an
    // Escape that reverts an edit must not also close the drawer.
    event.stopPropagation()
    if (event.key === 'Enter') {
      event.preventDefault()
      commit()
      event.target.blur()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      revert()
    }
  }

  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        justifyContent: 'flex-end', maxWidth: 112,
      }}
    >
      <TextField
        size='small'
        type='number'
        value={draft}
        onFocus={() => setEditing(true)}
        onChange={(event) => {
          setEditing(true)
          setDraft(event.target.value)
        }}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        // A focused number input mutates on scroll wheel. In a scrolling
        // table that is a silent edit to a billed weight, so the wheel
        // drops focus instead — the field then commits exactly as it would
        // have if the user had clicked away, and the wheel scrolls on.
        onWheel={(event) => {
          if (document.activeElement === event.target) event.target.blur()
        }}
        inputProps={{
          'aria-label': label,
          min: min == null ? 0 : min,
          step: step || 0.05,
          inputMode: 'decimal',
        }}
        sx={{
          width: 84,
          '& .MuiOutlinedInput-root': {bgcolor: WHITE, borderRadius: '8px'},
          '& .MuiOutlinedInput-notchedOutline': {borderColor: BORDER},
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: PRIMARY_BLUE, borderWidth: '1px',
          },
          // Doubled ampersand on purpose: the page shell (freeze-v2/index.js)
          // carries a `& .MuiOutlinedInput-input` rule at this same (0,2,0)
          // specificity, so a single `&` here would leave the winner to
          // stylesheet order. This cell's own spec should hold regardless.
          '&& .MuiInputBase-input': {
            fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: NAVY,
            textAlign: 'right', fontVariantNumeric: 'tabular-nums',
            py: 0.5, px: 0.75,
          },
          // Spinner arrows would take a third of an 84px cell and sit right
          // where the digits are. Arrow-key stepping by `step` is unaffected.
          '& input[type=number]': {MozAppearance: 'textfield'},
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none', margin: 0,
          },
        }}
      />
      {suffix && (
        <Box
          component='span'
          aria-hidden='true'
          sx={{flex: '0 0 auto', fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}
        >
          {suffix}
        </Box>
      )}
    </Box>
  )
}
